import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Shield } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@/features/auth/use-auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MOCK_USERS_LIST } from '@/lib/auth';
import { DEMO_PASSWORD, DEMO_USERS, DEMO_USER_CATEGORIES, type DemoCategory } from '@/lib/auth/demo-credentials';
import { env } from '@/lib/env';
import { cn } from '@/lib/utils/cn';

const schema = z.object({
  email: z.email('Correo invalido'),
  password: z.string().min(1, 'Ingresa tu contrasena'),
});
type FormValues = z.infer<typeof schema>;

export function LoginRoute() {
  const { signIn, status } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: env.showDemoCredentials ? DEMO_PASSWORD : '' },
  });

  async function onSubmit(values: FormValues) {
    try {
      await signIn(values.email, values.password);
      navigate('/app', { replace: true });
      toast.success('Sesion iniciada');
    } catch {
      toast.error('No pudimos iniciar sesion', {
        description: 'Revisa tu correo y contrasena e intenta nuevamente.',
      });
    }
  }

  function fillDemoUser(email: string) {
    form.setValue('email', email);
    form.setValue('password', 'demo');
  }

  function pickDemoUser(email: string) {
    form.setValue('email', email);
    form.setValue('password', DEMO_PASSWORD);
  }

  const groupedDemoUsers: Record<DemoCategory, typeof DEMO_USERS> = Object.fromEntries(
    DEMO_USER_CATEGORIES.map((cat) => [cat, DEMO_USERS.filter((u) => u.category === cat)]),
  ) as Record<DemoCategory, typeof DEMO_USERS>;

  const categoryLabels: Record<DemoCategory, string> = {
    plataforma: 'Plataforma',
    admin: 'Administradores',
    comite: 'Comité',
    conserje: 'Conserjería',
    propietario: 'Propietarios',
    residente: 'Residentes',
  };

  return (
    <div className="min-h-screen bg-[var(--color-paper)] text-[var(--color-ink)]">
      <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
        {/* Editorial side */}
        <aside className="relative hidden overflow-hidden bg-[var(--color-primary)] text-[var(--color-paper)] lg:flex lg:flex-col">
          {/* Decorative geometry */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-20"
            style={{
              backgroundImage:
                'radial-gradient(circle at 15% 20%, var(--color-paper) 0%, transparent 40%), radial-gradient(circle at 85% 80%, var(--color-accent) 0%, transparent 50%)',
            }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage:
                'linear-gradient(to right, transparent 0, transparent calc(100% - 1px), color-mix(in oklch, var(--color-paper) 8%, transparent) 100%)',
              backgroundSize: '80px 100%',
            }}
          />

          {/* Masthead */}
          <div className="relative z-10 flex items-center gap-3 px-12 pt-10">
            <svg width="44" height="44" viewBox="0 0 80 80" fill="none" aria-hidden>
              <rect x="17" y="18" width="12" height="44" rx="2" fill="white" opacity="0.95" />
              <rect x="51" y="18" width="12" height="44" rx="2" fill="white" opacity="0.95" />
              <rect x="17" y="34" width="46" height="12" rx="2" fill="#C98A2E" opacity="0.92" />
            </svg>
            <div>
              <p className="font-display text-2xl font-semibold tracking-tight">
                Habitia
              </p>
              <p className="text-[0.625rem] font-medium uppercase tracking-[0.18em] opacity-80">
                Administracion transparente
              </p>
            </div>
          </div>

          {/* Editorial hero */}
          <div className="relative z-10 flex flex-1 flex-col justify-center px-12 pb-10">
            <div className="flex items-center gap-3 text-[0.6875rem] font-semibold uppercase tracking-[0.18em] opacity-80">
              <span className="inline-block h-[7px] w-8 bg-[var(--color-paper)]" />
              <span>Numero 01 · Abril 2026</span>
            </div>
            <h1 className="font-display-tight mt-8 text-[4.25rem] font-semibold leading-[1.02] tracking-tight">
              El residente
              <span className="block font-semibold text-[var(--color-accent)]">
                confia tanto
              </span>
              como el admin.
            </h1>
            <p className="mt-8 max-w-md text-lg leading-relaxed opacity-85">
              Una plataforma donde los gastos comunes, el fondo de reserva y
              cada decision del comite son visibles para todos los
              copropietarios. Sin asteriscos.
            </p>

            <div className="mt-12 flex items-start gap-8 text-xs uppercase tracking-[0.14em] opacity-90">
              <div>
                <p className="font-display text-4xl font-semibold tabular">100%</p>
                <p className="mt-2">Trazabilidad por hash chain</p>
              </div>
              <div className="h-12 w-px bg-[var(--color-paper)] opacity-30" />
              <div>
                <p className="font-display text-4xl font-semibold tabular">0s</p>
                <p className="mt-2">Diferencia entre roles</p>
              </div>
            </div>
          </div>

          <footer className="relative z-10 flex items-center gap-2 border-t border-[color:color-mix(in_oklch,var(--color-paper)_15%,transparent)] px-12 py-5 text-[0.6875rem] uppercase tracking-[0.12em] opacity-75">
            <Shield className="h-3 w-3" />
            Alineado con la Ley 21.442 de Copropiedad Inmobiliaria
          </footer>
        </aside>

        {/* Form side */}
        <main className="flex items-center justify-center px-6 py-12 sm:px-12">
          <div className="w-full max-w-sm">
            <div className="mb-8 flex items-center gap-3 lg:hidden">
              <svg width="36" height="36" viewBox="0 0 80 80" fill="none" aria-hidden>
                <rect width="80" height="80" rx="18" fill="#0E6B7A" />
                <rect x="17" y="18" width="12" height="44" rx="2" fill="white" opacity="0.95" />
                <rect x="51" y="18" width="12" height="44" rx="2" fill="white" opacity="0.95" />
                <rect x="17" y="34" width="46" height="12" rx="2" fill="#C98A2E" opacity="0.92" />
              </svg>
              <p className="font-display text-xl font-bold tracking-tight text-[var(--color-ink)]">Habitia</p>
            </div>
            <p className="eyebrow">Acceso</p>
            <h2 className="font-display-tight mt-2 text-4xl font-semibold tracking-tight">
              Bienvenido de vuelta
            </h2>
            <p className="mt-2 text-sm text-[var(--color-ink-muted)]">
              Ingresa con tu correo institucional para acceder al panel.
            </p>

            <form onSubmit={form.handleSubmit(onSubmit)} className="mt-8 space-y-4">
              <div>
                <Label htmlFor="email">Correo electronico</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="tucorreo@condominio.cl"
                  className="mt-1.5"
                  {...form.register('email')}
                />
                {form.formState.errors.email && (
                  <p className="mt-1 text-xs text-[var(--color-danger)]">
                    {form.formState.errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Contrasena</Label>
                  <button
                    type="button"
                    className="text-xs text-[var(--color-primary)] hover:underline"
                  >
                    Olvidaste tu clave?
                  </button>
                </div>
                <div className="relative mt-1.5">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="demo"
                    className="pr-10"
                    {...form.register('password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-[var(--color-ink-faint)] hover:text-[var(--color-ink)]"
                    aria-label={showPassword ? 'Ocultar contrasena' : 'Mostrar contrasena'}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {form.formState.errors.password && (
                  <p className="mt-1 text-xs text-[var(--color-danger)]">
                    {form.formState.errors.password.message}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full"
                disabled={status === 'authenticating'}
              >
                {status === 'authenticating' ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Ingresando...
                  </>
                ) : (
                  'Ingresar'
                )}
              </Button>
            </form>

            {/* Demo user chips — solo en modo mocks.
                En modo Cognito real, los usuarios se crean con `pnpm --filter
                @condo/api seed` y se logean con su contrasena real. */}
            {env.useMocks ? (
              <div className="mt-10">
                <div className="flex items-center gap-3">
                  <span className="h-px flex-1 bg-[var(--color-hairline)]" />
                  <span className="text-[0.625rem] font-semibold uppercase tracking-[0.16em] text-[var(--color-ink-faint)]">
                    Demo rapida
                  </span>
                  <span className="h-px flex-1 bg-[var(--color-hairline)]" />
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  {MOCK_USERS_LIST.map((u) => {
                    const rolLabel = u.isSuperAdmin
                      ? 'super admin'
                      : Object.values(u.tenants)[0] ?? 'multi-tenant';
                    return (
                      <button
                        key={u.email}
                        type="button"
                        onClick={() => fillDemoUser(u.email)}
                        className={cn(
                          'group rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-3 text-left transition-all duration-150 ease-out hover:border-[var(--color-primary)] hover:bg-[var(--color-primary-50)]',
                        )}
                      >
                        <p className="text-[0.625rem] font-semibold uppercase tracking-[0.14em] text-[var(--color-ink-faint)] group-hover:text-[var(--color-primary)]">
                          {rolLabel.replace('_', ' ')}
                        </p>
                        <p className="mt-1 font-display text-sm font-semibold text-[var(--color-ink)]">
                          {u.name}
                        </p>
                        <p className="mt-0.5 font-mono text-[0.625rem] text-[var(--color-ink-muted)]">
                          {u.email}
                        </p>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-4 text-center text-[0.625rem] uppercase tracking-[0.14em] text-[var(--color-ink-faint)]">
                  Contrasena · <span className="font-mono normal-case">demo</span>
                </p>
              </div>
            ) : null}

            {env.showDemoCredentials && !env.useMocks ? (
              <div className="mt-10">
                <div className="flex items-center gap-3">
                  <span className="h-px flex-1 bg-[var(--color-hairline)]" />
                  <span className="text-[0.625rem] font-semibold uppercase tracking-[0.16em] text-[var(--color-ink-faint)]">
                    Usuarios demo (sandbox)
                  </span>
                  <span className="h-px flex-1 bg-[var(--color-hairline)]" />
                </div>
                <div className="mt-4 max-h-[360px] overflow-y-auto pr-1">
                  {DEMO_USER_CATEGORIES.map((cat) => {
                    const users = groupedDemoUsers[cat];
                    if (!users || users.length === 0) return null;
                    return (
                      <div key={cat} className="mb-4 last:mb-0">
                        <p className="mb-2 text-[0.625rem] font-semibold uppercase tracking-[0.14em] text-[var(--color-ink-faint)]">
                          {categoryLabels[cat]}
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          {users.map((u) => (
                            <button
                              key={u.email}
                              type="button"
                              onClick={() => pickDemoUser(u.email)}
                              className={cn(
                                'group rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-3 text-left transition-all duration-150 ease-out hover:border-[var(--color-primary)] hover:bg-[var(--color-primary-50)]',
                              )}
                            >
                              <p className="text-[0.625rem] font-semibold uppercase tracking-[0.14em] text-[var(--color-ink-faint)] group-hover:text-[var(--color-primary)]">
                                {u.rolLabel} · {u.condoLabel}
                              </p>
                              <p className="mt-1 font-display text-sm font-semibold text-[var(--color-ink)]">{u.nombre}</p>
                              <p className="mt-0.5 font-mono text-[0.625rem] text-[var(--color-ink-muted)]">{u.email}</p>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="mt-4 text-center text-[0.625rem] uppercase tracking-[0.14em] text-[var(--color-ink-faint)]">
                  Contraseña · <span className="font-mono normal-case">{DEMO_PASSWORD}</span>
                </p>
              </div>
            ) : null}
          </div>
        </main>
      </div>
    </div>
  );
}
